import React from "react";
import { Popover } from "@mui/material";
import { ChevronLeft } from "lucide-react";

import {
  CitationPassagesContext,
  needsPassage,
  openChatCitationWithPassage,
} from "@/components/common/citationPassages";

import {
  ChatCalculationInput,
  ChatCalculationSource,
  EvidenceSource,
  calculationInputMarker,
  chatSourceKind,
  chatSourceLabel,
} from "../evidenceFooter";
import { sourceKindLabel, sourceTarget } from "./sourceTargets";

// A recorded calculation's card (#13618): what a calculated figure's chip
// opens, beside the figure, instead of a viewer. A calculation has no
// document to show; the reader wants the arithmetic, and from each input its
// own source. Each input row opens exactly what that input's chip would.

interface CalculationCardProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  calculation: ChatCalculationSource;
  citationQueryId?: string;
  // The host page draws its own split-pane viewer (the agents run view,
  // #11987): an input opens there, not in Layout's global one.
  inlineViewerOnly?: boolean;
  // Called once an input row has opened its source, after the card closes:
  // a host that drew the card over a list closes the list too.
  onInputOpened?: () => void;
}

/** An input's own source, shaped as the sources list holds one. */
const inputSource = (
  input: ChatCalculationInput
): EvidenceSource | undefined => {
  const { entry } = input;
  if (!entry) return undefined;
  return {
    key: input.marker,
    number: 0,
    entry,
    markers: [input.marker],
    pages: typeof entry.pageNum === "number" ? [entry.pageNum] : [],
    calculation: input.calculation,
  };
};

const CalculationCard = ({
  anchorEl,
  onClose,
  calculation,
  citationQueryId,
  inlineViewerOnly,
  onInputOpened,
}: CalculationCardProps) => {
  const passages = React.useContext(CitationPassagesContext);
  // An input that is itself a calculation opens in this same card; the path
  // in is kept so the reader can step back out.
  const [path, setPath] = React.useState<ChatCalculationSource[]>([]);
  const shown = path[path.length - 1] ?? calculation;
  const parent =
    path.length > 1 ? path[path.length - 2] : path.length ? calculation : null;
  const close = () => {
    setPath([]);
    onClose();
  };

  const openInput = (input: ChatCalculationInput, source: EvidenceSource) => {
    if (input.calculation) {
      setPath([...path, input.calculation]);
      return;
    }
    const target = {
      ...sourceTarget(source, citationQueryId),
      // The value the calculation took from this source: the viewer looks
      // for it, as it does for a figure bound to its chip.
      displayText: input.value,
      inlineViewerOnly,
    };
    void openChatCitationWithPassage(
      target,
      passages && needsPassage(target)
        ? () => passages.passageFor(calculationInputMarker(input.marker))
        : undefined
    );
    close();
    onInputOpened?.();
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={close}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: {
            mt: 0.5,
            borderRadius: "10px",
            border: "1px solid var(--fp-border)",
            boxShadow: "var(--fp-lift)",
          },
        },
      }}
    >
      <div
        role="dialog"
        aria-label={shown.name}
        className="py-1 w-[340px] max-w-[calc(100vw-32px)]"
      >
        <div className="px-3 pt-1.5 pb-2 border-b border-[var(--fp-border)]">
          {parent && (
            <button
              type="button"
              onClick={() => setPath(path.slice(0, -1))}
              aria-label={`Back to ${parent.name}`}
              className="mb-1 -ml-1 flex max-w-full items-center gap-0.5 rounded-md px-1 py-0.5 text-[11.5px] text-[var(--fp-ink-3)] cursor-pointer hover:bg-[var(--fp-hover)] hover:text-[var(--fp-ink)] transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5 shrink-0" aria-hidden />
              <span className="truncate">{parent.name}</span>
            </button>
          )}
          <div className="text-[13px] font-medium text-[var(--fp-ink)] [overflow-wrap:anywhere]">
            {shown.name}
          </div>
          <div className="mt-1 font-mono text-[12px] text-[var(--fp-ink-3)] [overflow-wrap:anywhere]">
            {shown.expression
              ? `${shown.expression} = ${shown.result}`
              : shown.result}
          </div>
        </div>
        <ul className="pt-1">
          {shown.inputs.map((input, index) => {
            const source = inputSource(input);
            const value = (
              <span className="mt-0.5 shrink-0 rounded-[6px] bg-[#ECEDEF] px-1.5 py-0.5 font-mono text-[11.5px] text-[var(--fp-ink)]">
                {input.value}
              </span>
            );
            if (!source) {
              return (
                <li
                  key={`${input.marker}-${index}`}
                  className="flex items-start gap-2.5 px-3 py-1.5"
                >
                  {value}
                  <span className="min-w-0 pt-0.5 text-[11.5px] text-[var(--fp-ink-4)]">
                    Source not available
                  </span>
                </li>
              );
            }
            const named = (
              <>
                {value}
                <span className="min-w-0">
                  <span className="block text-[13px] text-[var(--fp-ink)] [overflow-wrap:anywhere]">
                    {chatSourceLabel(source.entry) || "Untitled source"}
                  </span>
                  <span className="block text-[11.5px] text-[var(--fp-ink-4)]">
                    {sourceKindLabel(source)}
                  </span>
                </span>
              </>
            );
            // A calculation input the card is already inside of (A's input
            // is B, B's input is A) is named, never re-entered.
            if (
              chatSourceKind(source.entry) === "calculation" &&
              !input.calculation
            ) {
              return (
                <li
                  key={`${input.marker}-${index}`}
                  className="flex items-start gap-2.5 px-3 py-1.5"
                >
                  {named}
                </li>
              );
            }
            return (
              <li key={`${input.marker}-${index}`}>
                <button
                  type="button"
                  onClick={() => openInput(input, source)}
                  className="w-full flex items-start gap-2.5 text-left px-3 py-1.5 cursor-pointer hover:bg-[var(--fp-band-hover)] transition-colors"
                >
                  {named}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </Popover>
  );
};

export default CalculationCard;
